import { Button } from '@mui/material';
import { Link, LinkProps } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { SYNMATCH_COLORS, SYNMATCH_RADII } from 'theme';

export const Journey = styled.div`
  display: flex;
  flex-direction: column;
`;

export const PhaseBlock = styled.div`
  & + & {
    margin-top: 2.8rem;
  }
`;

export const PhaseHead = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

export const PhaseTag = styled.span`
  font-size: 0.74rem;
  font-weight: 580;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${SYNMATCH_COLORS.primary};
  white-space: nowrap;
`;

export const PhaseRule = styled.span`
  flex: 1;
  height: 0.0625rem;
  background: ${SYNMATCH_COLORS.border.default};
`;

export const StepGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);
  column-gap: 2.6rem;
  row-gap: 0;

  @media (max-width: 48rem) {
    grid-template-columns: repeat(2, 1fr);
    column-gap: 2rem;
  }

  @media (max-width: 30rem) {
    grid-template-columns: 1fr;
  }
`;

export const StepCard = styled.div`
  min-width: 0;
`;

export const StepNumber = styled.p`
  margin: 0;
  font-size: 0.78rem;
  font-weight: 560;
  letter-spacing: 0.12em;
  font-variant-numeric: tabular-nums;
  color: ${SYNMATCH_COLORS.text.muted};
`;

export const StepName = styled.h3`
  margin: 0.9rem 0 0.5rem;
  font-size: 1.15rem;
  font-weight: 560;
  letter-spacing: -0.018em;
  color: ${SYNMATCH_COLORS.primary};
`;

export const StepBody = styled.p`
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.5;
  color: ${SYNMATCH_COLORS.text.muted};
`;

export const HeroSection = styled.section`
  padding-block: clamp(4rem, 8vw, 7rem) clamp(4rem, 7vw, 6rem);
`;

export const SectionEyebrow = styled.p`
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.72rem;
  font-weight: 560;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${SYNMATCH_COLORS.text.muted};

  &::before {
    content: '';
    width: 1.6rem;
    height: 0.0625rem;
    background: ${SYNMATCH_COLORS.border.default};
    flex-shrink: 0;
  }
`;

export const HeroEyebrow = styled(SectionEyebrow)`
  &::before {
    width: 2.1rem;
    height: 0.125rem;
    background: ${SYNMATCH_COLORS.cerise};
  }
`;

export const HeroTop = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: clamp(1.5rem, 4vw, 3.5rem);
  flex-wrap: wrap;
  margin-top: 2.1rem;

  @media (max-width: 48rem) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.4rem;
  }
`;

export const HeroHeadline = styled.h1`
  flex: 1 1 22rem;
  margin: 0;
  font-size: clamp(2.7rem, 7vw, 5.25rem);
  line-height: 0.98;
  font-weight: 640;
  letter-spacing: -0.03em;
  color: ${SYNMATCH_COLORS.primary};
  text-wrap: balance;

  em {
    font-style: normal;
    color: ${SYNMATCH_COLORS.text.default};
  }

  @media (max-width: 48rem) {
    flex: none;
  }

  @media (max-width: 30rem) {
    font-size: clamp(2.4rem, 11vw, 3.4rem);
  }
`;

export const HeroCtaCluster = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem 1.5rem;
  flex-shrink: 0;
  margin-bottom: 0.55rem;

  @media (max-width: 48rem) {
    margin-bottom: 0;
  }
`;

export const HeroCtaButton = styled(Button).attrs({
  color: 'primary',
  variant: 'contained',
})<{ component?: React.ElementType; to?: LinkProps['to'] }>`
  && {
    padding: 1rem 1.7rem;
    font-size: 1rem;
    font-weight: 520;
    letter-spacing: -0.012em;
    border-radius: ${SYNMATCH_RADII.md};

    .MuiSvgIcon-root {
      font-size: 1.2rem;
      transition: transform 160ms cubic-bezier(0.2, 0, 0.2, 1);
    }

    &:hover .MuiSvgIcon-root {
      transform: translateX(0.1875rem);
    }
  }
`;

export const HeroSubcopy = styled.p`
  margin: 2.4rem 0 0;
  font-size: clamp(1.05rem, 1.6vw, 1.3rem);
  color: ${SYNMATCH_COLORS.text.muted};
  max-width: 52rem;
  line-height: 1.5;
  letter-spacing: -0.012em;
`;

const tintedBand = css`
  background: ${SYNMATCH_COLORS.surface.app};
  border-block: 0.0625rem solid ${SYNMATCH_COLORS.border.default};
`;

export const ContentSection = styled.section<{ $tint?: boolean }>`
  padding-block: clamp(3rem, 6vw, 5.5rem);

  ${({ $tint }) => ($tint ? tintedBand : null)}
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 2.2rem;
  flex-wrap: wrap;
`;

export const SectionTitle = styled.h2`
  margin: 0.7rem 0 0;
  font-size: clamp(1.5rem, 3vw, 2.1rem);
  font-weight: 600;
  letter-spacing: -0.024em;
  line-height: 1.1;
  color: ${SYNMATCH_COLORS.primary};
`;

export const SectionTextLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  text-decoration: none;
  color: ${SYNMATCH_COLORS.primary};
  font-weight: 520;
  font-size: 0.95rem;
  letter-spacing: -0.01em;
  border-radius: ${SYNMATCH_RADII.sm};
  transition: gap 160ms cubic-bezier(0.2, 0, 0.2, 1);

  .MuiSvgIcon-root {
    font-size: 1.1rem;
  }

  &:hover {
    gap: 0.7rem;
  }

  &:focus-visible {
    outline: 0.125rem solid ${SYNMATCH_COLORS.primary};
    outline-offset: 0.25rem;
  }
`;

export const CompaniesRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem 2rem;
  flex-wrap: wrap;
`;

export const CompaniesText = styled.div`
  max-width: 42rem;
`;

export const CompaniesBody = styled.p`
  margin: 0.5rem 0 0;
  color: ${SYNMATCH_COLORS.text.muted};
  font-size: 0.98rem;
  line-height: 1.5;
`;
